import axiosInstance from "./axiosConfig";
import { handleAPIError } from "./utils/errors";

export type IntegritySeverity = "must" | "should" | "info";

/** One item the agent left for later (`case_health` issue, deferred). */
export type FollowUpItem = {
  id: string;
  kind: string;
  severity: IntegritySeverity | string;
  subject: string;
  cause: string;
  fix_with?: string[];
  /** Why it was left: declined by the attorney, another case, end of turn. */
  reason?: string | null;
  message_id?: string | null;
};

/** A case's follow-ups; `own` is the thread's case, the rest are the client's other active cases. */
export type FollowUpGroup = {
  individual_id: string;
  own: boolean;
  /** Another case's visa and applicant, when the server knows them. */
  visa?: string | null;
  applicant?: string | null;
  items: FollowUpItem[];
};

const call = async <T>(operation: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const response = await operation();
    return response.data;
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

export const CaseIntegrityAPI = {
  /** What one reply (or a whole thread) left for later, grouped by case. */
  async getFollowUps(
    individualId: string,
    options: { threadId?: string | null; messageId?: string | null } = {}
  ): Promise<FollowUpGroup[]> {
    const data = await call<{ groups: FollowUpGroup[] }>(() =>
      axiosInstance.get(`/case-integrity/${individualId}/follow-ups`, {
        params: {
          ...(options.threadId ? { thread_id: options.threadId } : {}),
          ...(options.messageId ? { message_id: options.messageId } : {})
        }
      })
    );
    return data.groups ?? [];
  },

  /** "Looks right": ignore the item until its subject changes again. */
  dismiss(individualId: string, issueId: string) {
    return call<{ dismissed: string }>(() =>
      axiosInstance.post(`/case-integrity/${individualId}/dismiss`, {
        issue_id: issueId
      })
    );
  }
};
